import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { UserCheck, UserX, Mail, Building2, Briefcase, Users, Clock, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";

const ACCOUNT_META = {
  trader: { label: "Trader", color: "text-blue-700", Icon: Briefcase },
  real_estate: { label: "Real Estate", color: "text-emerald-700", Icon: Users },
  studio: { label: "Studio", color: "text-gold", Icon: Building2 },
};

function accountMeta(t) {
  return ACCOUNT_META[t] || ACCOUNT_META.studio;
}

export default function AdminApprovals() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/admin/users/pending");
      setUsers(data);
    } catch {
      toast.error("Errore caricamento richieste");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  async function approve(u) {
    try {
      await api.put(`/admin/users/${u.id}/approve`);
      toast.success(`Accesso approvato per ${u.studio || u.email}`);
      await reload();
    } catch { toast.error("Errore approvazione"); }
  }

  async function reject(u) {
    if (!window.confirm(`Rifiutare la richiesta di ${u.studio || u.email}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}/reject`);
      toast.success("Richiesta rifiutata");
      await reload();
    } catch { toast.error("Errore"); }
  }

  return (
    <div className="space-y-4" data-testid="admin-approvals">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-slate1">Approvazioni</div>
          <div className="font-display text-2xl text-ink">Nuove iscrizioni in attesa</div>
        </div>
        <div className="text-[11px] tracking-[0.15em] uppercase text-slate1">{users.length} in attesa</div>
      </div>

      {loading ? (
        <div className="bg-white border border-ink/10 rounded-sm p-6 text-sm text-slate1">Caricamento...</div>
      ) : users.length === 0 ? (
        <div className="bg-white border border-ink/10 rounded-sm p-8 text-center" data-testid="admin-approvals-empty">
          <UserCheck size={28} className="mx-auto text-slate1/60 mb-3" />
          <div className="text-sm text-slate1">Nessuna richiesta in sospeso. Tutte le iscrizioni sono state gestite.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {users.map((u) => {
            const m = accountMeta(u.account_type);
            const Icon = m.Icon;
            return (
            <div key={u.id} className="bg-white border border-ink/10 rounded-sm p-5" data-testid={`approval-${u.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className={`text-[10px] tracking-[0.2em] uppercase ${m.color} inline-flex items-center gap-1.5`}>
                    <Icon size={11} />
                    {m.label}
                  </div>
                  <div className="mt-1 font-display text-lg text-ink truncate">{u.name}</div>
                  <div className="text-sm text-slate1 truncate">{u.studio || "—"}</div>
                </div>
                <div className="text-[10px] tracking-[0.15em] uppercase text-slate1 flex items-center gap-1">
                  <Clock size={10} /> {new Date(u.created_at).toLocaleDateString("it-IT")}
                </div>
              </div>

              <div className="mt-3 flex items-center gap-1.5 text-xs text-slate1 truncate">
                <Mail size={12} /> {u.email}
              </div>

              {Array.isArray(u.possible_duplicates) && u.possible_duplicates.length > 0 && (
                <div
                  className="mt-3 border border-amber-300 bg-amber-50 rounded-sm p-3"
                  data-testid={`approval-duplicates-${u.id}`}
                >
                  <div className="text-[10px] tracking-[0.2em] uppercase text-amber-700 inline-flex items-center gap-1.5">
                    <AlertTriangle size={11} /> Possibile duplicato · {u.possible_duplicates.length}
                  </div>
                  <ul className="mt-2 space-y-1">
                    {u.possible_duplicates.map((d) => (
                      <li key={d.id} className="text-[11px] text-amber-900 truncate">
                        <span className="font-medium">{d.name || "—"}</span>
                        {d.studio ? ` · ${d.studio}` : ""}
                        <span className="text-amber-700"> · {d.email}</span>
                        <span className="ml-1 uppercase tracking-[0.15em] text-[9px] text-amber-700">
                          [{accountMeta(d.account_type).label}{d.approved ? "" : " · in attesa"}]
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-5 flex gap-2">
                <button
                  onClick={() => approve(u)}
                  className="flex-1 bg-ink text-cream py-2.5 text-[11px] tracking-[0.2em] uppercase rounded-sm inline-flex items-center justify-center gap-2 hover-lift"
                  data-testid={`approval-approve-${u.id}`}
                >
                  <UserCheck size={14} /> Approva
                </button>
                <button
                  onClick={() => reject(u)}
                  className="px-4 py-2.5 border border-ink/20 text-slate1 text-[11px] tracking-[0.2em] uppercase rounded-sm hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30 inline-flex items-center gap-2"
                  data-testid={`approval-reject-${u.id}`}
                >
                  <UserX size={14} /> Rifiuta
                </button>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
